import React, { useState } from "react";
import { Rating, Stack, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import ButtonLoading from "../../Button/ButtonLoading";
import _head from "lodash/head";
import _filter from "lodash/filter";
import CustomerFeedbackProvenExpertBox from "./CustomerFeedbackRatingPlatformBox";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCollection, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import _set from "lodash/set";
import _merge from "lodash/merge";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import ProductBox from "@/src/components/ProductBox/ProductBox";

export default function CustomerFeedbackBox({ product }: { product: AbstractCase }) {
  const urlParams = {
    source: "fullReview",
    case: `/abstract_cases/${product.id}`,
  };
  const { data: feedbackCollection, isLoading: queryIsLoading } = useQuery({
    queryKey: queryKeys.collection("/customer_feedbacks", urlParams),

    queryFn: () => fetchCollection("/customer_feedbacks", urlParams),
  });
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [values, setValues] = useState({
    stars: 0,
    comment: "",
  });

  if (queryIsLoading || !feedbackCollection) {
    return null;
  }

  const filteredFeedbackCollection = _filter(
    feedbackCollection,
    (feedbackEntity) => !feedbackEntity.feedbackDateTime || feedbackEntity.showProvenExpertRatingRequest,
  );

  if (filteredFeedbackCollection.length === 0) {
    return null;
  }

  const currentFeedback = _head(filteredFeedbackCollection);
  const handleSubmit = async () => {
    setIsLoading(true);

    let payload: { stars: number; comment: string; showProvenExpertRatingRequest?: boolean } = {
      stars: values.stars,
      comment: values.comment,
    };

    if (values.stars === 5 && currentFeedback.isFinalReview) {
      payload.showProvenExpertRatingRequest = true;
    }

    await updateMutation.mutateAsync({
      uri: "/customer_feedbacks",
      id: currentFeedback.id,
      data: payload,
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customer_feedbacks", urlParams),
    });
    setIsLoading(false);
  };

  if (currentFeedback.showProvenExpertRatingRequest) {
    return <CustomerFeedbackProvenExpertBox product={product} customerFeedback={currentFeedback} />;
  }

  const handleChangeComment = ({ target }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues((prevData) => {
      return _set(_merge({}, prevData), "comment", target.value);
    });
  };

  const handleChangeRating = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setValues((prevData) => {
      return _set(_merge({}, prevData), "stars", parseInt(target.value));
    });
  };

  return (
    <ProductBox headline={"Bitte bewerten Sie uns"}>
      <Typography>Wie zufrieden waren Sie bisher mit dem durch uns erbrachten Service? Wir freuen uns über Ihre Bewertung.</Typography>
      <Rating
        sx={{ margin: "1rem 0" }}
        name="stars"
        value={values.stars}
        onChange={(e) => handleChangeRating(e as React.ChangeEvent<HTMLInputElement>)}
        size="large"
      />
      <TextField
        sx={{ marginBottom: "1rem" }}
        label="Ihr Kommentar"
        name="comment"
        value={values.comment}
        onChange={handleChangeComment}
        multiline
        rows="4"
        maxRows="4"
        margin="normal"
        fullWidth
      />
      <Stack>
        <ButtonLoading
          sx={{ marginLeft: { md: "auto" } }}
          color="primary"
          variant="outlined"
          isLoading={isLoading}
          disabled={values.stars < 1}
          onClick={handleSubmit}
        >
          Bewertung abgeben
        </ButtonLoading>
      </Stack>
    </ProductBox>
  );
}
